import { StatusCodes } from 'http-status-codes';
import { z } from 'zod';
import api from '~/utils/api';
import { ResponseError } from '~/utils/errors';
import { addBreadcrumb } from '~/utils/observability';
import { MiddlewareFunction } from '~/utils/routing';
import { storeParamSchema } from '~/utils/schemas';

const paramsSchema = z.object({
  idFromPlatform: z.string(),
  store: storeParamSchema.shape.store.optional(),
});

type AppStore = NonNullable<Parameters<MiddlewareFunction>[1]['appStore']>;
type State = ReturnType<AppStore['getState']>;

const getOrder = (
  { idFromPlatform, store }: z.infer<typeof paramsSchema>,
  settings: State['settings'],
) =>
  store && settings ?
    api.fetchCrewOrder({
      params: { storefrontId: settings.storefrontId },
      query: { idFromPlatform },
    })
  : api.fetchProtectionOrder({ query: { idFromPlatform } });

const isValidOrder = (
  params: z.infer<typeof paramsSchema>,
  order: State['order'],
) =>
  !!order &&
  order.idFromPlatform === params.idFromPlatform &&
  (params.store === undefined || params.store === order.storeSlug);

// enable the order to be available to the app shell via the app context
export const middleware: MiddlewareFunction = async ({ params }, ctx) => {
  const { appStore } = ctx;

  try {
    const validParams = paramsSchema.parse(params);
    const state = appStore.getState();

    if (!isValidOrder(validParams, state.order)) {
      // clear the current invalid order
      appStore.dispatch(appStore.actions.invalidateOrder());

      const order = await getOrder(validParams, state.settings);

      appStore.dispatch(appStore.actions.setOrder(order));
    }

    return ctx;
  } catch (cause) {
    if (
      cause instanceof ResponseError &&
      cause.response.status === StatusCodes.NOT_FOUND
    ) {
      addBreadcrumb({
        type: 'info',
        level: 'info',
        category: 'OrderMiddleware',
        message: cause.message,
        data: { params },
      });
      return ctx;
    }

    throw new Error('Order Middleware error', { cause });
  }
};
